import { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom";
import { Upload, AlertCircle, CheckCircle, FileCheck2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Alert, AlertDescription } from "../components/ui/alert";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ALLOWED_TYPES = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];
const MAX_BYTES = 25 * 1024 * 1024;

const ProofOfPaymentForm = () => {
  const { orderId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    full_name: "", email: "", phone: "",
    amount_transferred: "", date_of_transfer: "",
    bank_used: "", paid_to_account: "", transaction_reference: "", notes: "",
  });
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/orders/${orderId}`, { params: { token } });
        setOrder(res.data);
        setForm((p) => ({
          ...p,
          full_name: res.data.customer_name || "",
          email: res.data.customer_email || "",
          phone: res.data.customer_phone || "",
          amount_transferred: res.data.amount?.toString() || "",
        }));
      } catch (err) {
        setError(err.response?.data?.detail || "Order not found or token invalid");
      } finally {
        setLoading(false);
      }
    })();
  }, [orderId, token]);

  const validate = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = "Required";
    if (!form.email.trim()) e.email = "Required";
    if (!form.amount_transferred || isNaN(parseFloat(form.amount_transferred))) e.amount_transferred = "Valid amount required";
    if (!form.date_of_transfer) e.date_of_transfer = "Required";
    if (!form.bank_used.trim()) e.bank_used = "Required";
    if (!form.paid_to_account) e.paid_to_account = "Please choose the account you paid into";
    if (!form.transaction_reference.trim()) e.transaction_reference = "Required";
    if (!file) e.file = "Please upload your proof of payment";
    else {
      if (!ALLOWED_TYPES.includes(file.type)) e.file = "File must be JPG, PNG, or PDF";
      else if (file.size > MAX_BYTES) e.file = "File must be under 25 MB";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("token", token);
      Object.entries(form).forEach(([k, v]) => v != null && fd.append(k, v));
      fd.append("file", file);
      const res = await axios.post(`${API}/orders/${orderId}/proof`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data.success) {
        toast.success("Proof submitted! We'll verify and email you shortly.");
        navigate(`/orders/${orderId}?token=${token}`);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "Submission failed";
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-slate-50 p-8"><div className="container mx-auto max-w-3xl animate-pulse"><div className="h-96 skeleton rounded-xl" /></div></div>;
  }
  if (error || !order) {
    return (
      <div className="min-h-screen bg-slate-50 p-8">
        <div className="container mx-auto max-w-3xl">
          <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{error || "Order not found"}</AlertDescription></Alert>
        </div>
      </div>
    );
  }

  const orderShort = `#${order.id.slice(0, 8).toUpperCase()}`;

  if (order.status === "proof_submitted" || order.status === "paid") {
    return (
      <div className="min-h-screen bg-slate-50 py-12">
        <div className="container mx-auto max-w-3xl">
          <Card>
            <CardContent className="p-10 text-center">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h2 className="font-heading font-bold text-2xl mb-2">Proof already submitted</h2>
              <p className="text-muted-foreground mb-6">{order.status === "paid" ? "Your enrollment is active." : "We are verifying your payment."}</p>
              <Link to={`/orders/${order.id}?token=${token}`}>
                <Button className="bg-brand hover:bg-brand-dark text-white">View My Order</Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  return (
    <div className="min-h-screen bg-slate-50 py-10">
      <div className="container mx-auto px-4 lg:px-8 max-w-3xl">
        <Card>
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <FileCheck2 className="h-5 w-5 text-brand" /> Submit Proof of Payment
            </CardTitle>
            <p className="text-sm text-muted-foreground">Order {orderShort} — {order.course_title}</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="proof-form">
              <Field label="Full Name" id="full_name" error={errors.full_name}>
                <Input id="full_name" value={form.full_name} onChange={set("full_name")} data-testid="proof-full-name-input" />
              </Field>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Email" id="email" error={errors.email}>
                  <Input id="email" type="email" value={form.email} onChange={set("email")} data-testid="proof-email-input" />
                </Field>
                <Field label="Phone" id="phone">
                  <Input id="phone" value={form.phone} onChange={set("phone")} data-testid="proof-phone-input" />
                </Field>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Amount Transferred" id="amount_transferred" error={errors.amount_transferred} hint="USD or JMD value as entered on transfer">
                  <Input id="amount_transferred" type="number" step="0.01" value={form.amount_transferred} onChange={set("amount_transferred")} data-testid="proof-amount-input" />
                </Field>
                <Field label="Date of Transfer" id="date_of_transfer" error={errors.date_of_transfer}>
                  <Input id="date_of_transfer" type="date" value={form.date_of_transfer} onChange={set("date_of_transfer")} data-testid="proof-date-input" />
                </Field>
              </div>
              <Field label="Which of our accounts did you pay into?" id="paid_to_account" error={errors.paid_to_account}>
                <div className="grid sm:grid-cols-3 gap-2" data-testid="proof-paid-to-group">
                  {[["jmmb_usd", "JMMB Jamaica — USD"], ["jmmb_jmd", "JMMB Jamaica — JMD"], ["wells_fargo_us", "Wells Fargo (US) — USD"]].map(([v, l]) => (
                    <button type="button" key={v} onClick={() => setForm((f) => ({ ...f, paid_to_account: v }))}
                      className={`border rounded-lg p-3 text-sm text-left transition-colors ${form.paid_to_account === v ? "border-brand bg-brand/5 ring-2 ring-brand/20 font-medium" : "hover:border-brand/50"}`}
                      data-testid={`proof-paid-to-${v}`}>{l}</button>
                  ))}
                </div>
              </Field>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Your Bank (sending bank)" id="bank_used" error={errors.bank_used}>
                  <Input id="bank_used" placeholder="e.g. NCB, Chase, Trustmark" value={form.bank_used} onChange={set("bank_used")} data-testid="proof-bank-input" />
                </Field>
                <Field label="Transaction Reference Number" id="transaction_reference" error={errors.transaction_reference}>
                  <Input id="transaction_reference" value={form.transaction_reference} onChange={set("transaction_reference")} data-testid="proof-reference-input" />
                </Field>
              </div>
              <Field label="Optional Notes" id="notes">
                <Textarea id="notes" rows={3} value={form.notes} onChange={set("notes")} data-testid="proof-notes-input" />
              </Field>
              <Field label="Upload Proof of Payment" id="file" error={errors.file} hint="JPG, JPEG, PNG, or PDF (max 25MB)">
                <div className="flex items-center gap-3">
                  <input
                    id="file"
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf"
                    onChange={(e) => { setFile(e.target.files?.[0] || null); setErrors((p) => ({ ...p, file: null })); }}
                    className="flex-1 file:bg-brand file:text-white file:rounded file:px-3 file:py-1 file:border-0 file:mr-3"
                    data-testid="proof-file-input"
                  />
                </div>
                {file && <p className="text-xs text-muted-foreground mt-1">{file.name} ({(file.size / 1024).toFixed(1)} KB)</p>}
              </Field>
              <Button type="submit" disabled={submitting} className="w-full bg-brand hover:bg-brand-dark text-white text-lg py-6" data-testid="proof-submit-btn">
                {submitting ? "Submitting..." : <><Upload className="mr-2 h-4 w-4" /> Submit Proof of Payment</>}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const Field = ({ label, id, error, hint, children }) => (
  <div className="space-y-2">
    <Label htmlFor={id}>{label}</Label>
    {children}
    {hint && !error && <p className="text-xs text-muted-foreground">{hint}</p>}
    {error && <p className="text-sm text-destructive">{error}</p>}
  </div>
);

export default ProofOfPaymentForm;
